import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  X, Copy, Check, Lock, CheckCircle2, ChevronLeft, ChevronRight, ChevronDown,
  Mail, FileText, ExternalLink, Loader2, ShieldCheck, Send, BellOff, RefreshCw,
  AlertTriangle, CalendarDays, Plane, Stethoscope, Landmark, MessageSquare,
  History as HistoryIcon, User as UserIcon, Clock3, GraduationCap, Briefcase,
  Paperclip, Eye, Upload,
} from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import { STAGE_ORDER, STAGE_LABELS } from '../../../lib/workflow/transitions';
import { OverrideModal } from './OverrideModal';
import { ApproveOverrideModal } from './ApproveOverrideModal';
import { DemoteModal } from './DemoteModal';
import { StatusSignal } from './StatusSignal';
import { AssignAutomationModal } from './AssignAutomationModal';
import { AdminActionGatesPanel } from './AdminActionGatesPanel';
import { DrawerTasksSection } from './DrawerTasksSection';
import { DrawerActivityFeed } from './DrawerActivityFeed';
import { DrawerAssignedWorkflows } from './DrawerAssignedWorkflows';
import { Sparkles } from 'lucide-react';
import StageGatePanel, { advanceButtonLabel, shouldShowAdvanceButton } from './StageGatePanel';
import StageFocusContent from './StageFocusContent';
import { AiDraftEmailModal } from './AiDraftEmailModal';
import { CreateInternalTaskModal } from './CreateInternalTaskModal';
import { ApplicantAdminActions } from './ApplicantAdminActions';
import { PiiText } from '../../../lib/piiDisplay';
import { joinPersonName, sanitizeNamePart } from '../../../lib/nameUtils';
// Shared email composer — the SAME foundation the ApplicantDetail Email
// tab and the BatchCenter campaign wizard build on (one code path for
// template insertion, WYSIWYG editing, merge fields, document templates).
import { useComposerBody } from '../email/useComposerBody';
import { ComposerBody } from '../email/ComposerBody';
import { sanitizeEmailHtml } from '../email/emailSanitize';
import { MERGE_FIELD_CATEGORIES } from '../email/mergeFieldCategories';
import { newIdempotencyKey, type AttachmentPayload } from '../email/emailApi';
import { BroadcastJobProgress } from '../email/BroadcastJobProgress';
import TemplatePickerModal from '../TemplatePickerModal';
import DocumentTemplateMultiSelect from '../../shared/DocumentTemplateMultiSelect';
import MergeFieldPicker from '../../shared/MergeFieldPicker';
import { resolveMergeFields } from '../../../lib/variables/resolver';
import type { ApplicantFull, FullApplicantRecord, PreApprovalCheck, UnmetGate } from './types';

interface ApplicantDrawerProps {
  uid: string | null;
  /** Uids in the current list, for J/K navigation. */
  uids?: string[];
  onClose: () => void;
  onNavigate?: (uid: string) => void;
  onViewApplicant?: (app: any) => void;
  /** Called after any successful mutation so the parent refreshes. */
  onChanged: () => void;
  /** Whether the viewer may use the override flow (server still enforces). Defaults true. */
  isSeniorAdmin?: boolean;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?';
}

/**
 * Resolve the applicant's photo URL the same way ApplicantChatView does:
 * top-level fields first, then the personalDetails copies.
 */
function resolvePhotoUrl(application: any): string {
  if (!application) return '';
  const pd = application.personalDetails || {};
  return (
    application.profilePhotoUrl ||
    application.photoUrl ||
    pd.profilePhotoUrl ||
    pd.profilePhoto ||
    ''
  );
}

/** Header avatar: the applicant's photo, falling back to initials when
 *  there is no photo or the image URL fails to load. */
function DrawerAvatar({ photoUrl, name }: { photoUrl: string; name: string }) {
  const [failed, setFailed] = useState(false);
  if (photoUrl && !failed) {
    return (
      <div className="h-12 w-12 rounded-2xl overflow-hidden bg-slate-100 shrink-0">
        <img src={photoUrl} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      </div>
    );
  }
  return (
    <div className="h-12 w-12 rounded-2xl bg-brand text-white flex items-center justify-center text-sm font-extrabold shrink-0" aria-hidden="true">
      {initials(name)}
    </div>
  );
}

/** "Jan 2020 – Present"-style period label for education / work entries,
 *  which store either ISO start/end dates or month/year field pairs. */
function entryPeriod(e: any): string {
  const fmt = (v: any): string => {
    if (!v) return '';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
  };
  const start = fmt(e?.startDate) || [e?.startMonth, e?.startYear].filter(Boolean).join(' ');
  const end = fmt(e?.endDate) || [e?.endMonth, e?.endYear].filter(Boolean).join(' ') || (e?.isEmployed ? 'Present' : '');
  return [start, end].filter(Boolean).join(' – ');
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function fmtClock(ts: number | null): string {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Money with its currency code — drawer amounts are never bare numbers (PR-20). */
function fmtMoney(n: number, currency?: string | null): string {
  return `${String(currency || 'USD').toUpperCase()} ${Number(n || 0).toFixed(2)}`;
}

function Section({ title, icon, children, defaultOpen = true }: { title: string; icon: React.ReactNode; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl shadow-2xs overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-slate-50/60"
      >
        <span className="text-slate-500">{icon}</span>
        <span className="text-sm font-extrabold text-slate-900 tracking-tight">{title}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 ml-auto transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-slate-100">{children}</div>}
    </div>
  );
}

const CHECK_STATE_TONE: Record<PreApprovalCheck['state'], string> = {
  pass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  review: 'bg-sky-50 text-sky-800 border-sky-200',
  not_applicable: 'bg-slate-100 text-slate-500 border-slate-200',
};

/**
 * Document-status chip tones. The map is a TONE lookup ONLY — the label
 * is always the raw status value, and any status the map doesn't know
 * (new statuses are added over time, e.g. by the document-review lane)
 * falls back to the neutral tone with its raw label rendered verbatim.
 * An unknown status must never crash the drawer or render blank.
 */
const DOC_STATUS_TONES: Record<string, string> = {
  FurtherReviewRequested: 'bg-violet-100 text-violet-700',
  Approved: 'bg-emerald-50 text-emerald-700',
  'Approved Admin': 'bg-emerald-50 text-emerald-700',
  Verified: 'bg-emerald-50 text-emerald-700',
  Rejected: 'bg-red-50 text-red-700',
  SystemPreApproved: 'bg-sky-50 text-sky-700',
  PendingAdminReview: 'bg-amber-50 text-amber-800',
  Pending: 'bg-slate-100 text-slate-600',
};

function docStatusTone(status: string): string {
  return DOC_STATUS_TONES[status] || 'bg-slate-100 text-slate-600';
}

interface NudgeModalItem {
  uid: string;
  applicationId: string;
  displayName?: string;
  email?: string;
}

interface BulkEmailOutcome {
  uid: string;
  applicationId: string;
  status: 'queued' | 'rejected';
  reason?: string;
}

/**
 * Workflow status-email composer — built on the portal's SHARED email
 * composer (the same useComposerBody/ComposerBody + TemplatePickerModal +
 * DocumentTemplateMultiSelect foundation as the ApplicantDetail Email tab
 * and the BatchCenter campaign wizard). Sending dispatches ONE bulk-email
 * job via POST /api/admin/workflow/bulk-email: the server resolves merge
 * fields per recipient against their own application and generates the
 * selected document templates per recipient (a generation failure fails
 * only that recipient — their email is never sent bare). The preview
 * below resolves the dynamic fields for a chosen SAMPLE recipient so the
 * admin sees real values, not raw {{tokens}}, before sending.
 */
function NudgeModal({ items, onClose, onSent }: { items: NudgeModalItem[]; onClose: () => void; onSent: () => void }) {
  const [nudgeType, setNudgeType] = useState('status_update');
  const [subject, setSubject] = useState('');
  const [documentTemplateIds, setDocumentTemplateIds] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<AttachmentPayload[]>([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [attachError, setAttachError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [outcomes, setOutcomes] = useState<BulkEmailOutcome[] | null>(null);
  const [result, setResult] = useState<{ jobId: string; status: string; recipientCount: number; deduplicated?: boolean } | null>(null);
  // One idempotency key per compose session; reused across retries of the
  // same send action so a double submit can never queue two jobs.
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Shared composer — template insertion forwards the template's subject
  // and its carried document templates into this modal's state, exactly
  // like the Email tab and the campaign wizard.
  const composer = useComposerBody({
    onTemplateSubject: (s) => setSubject(s),
    onTemplateDocumentTemplates: (ids) => setDocumentTemplateIds(ids),
  });

  // ── Preview sample (BatchCenter pattern): resolve against ONE selected
  // recipient's real application record, fetched from the drawer's /full
  // contract and cached per uid.
  const [sampleUid, setSampleUid] = useState(items[0]?.uid || '');
  const [sampleApp, setSampleApp] = useState<any | null>(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const sampleCacheRef = useRef<Map<string, any>>(new Map());
  const sampleItem = items.find((i) => i.uid === sampleUid) || items[0];

  useEffect(() => {
    if (!sampleUid) return;
    const cached = sampleCacheRef.current.get(sampleUid);
    if (cached) {
      setSampleApp(cached);
      return;
    }
    let cancelled = false;
    setSampleLoading(true);
    (async () => {
      try {
        const data: any = await safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(sampleUid)}/full`);
        if (cancelled) return;
        const app = data?.application || null;
        if (app) sampleCacheRef.current.set(sampleUid, app);
        setSampleApp(app);
      } catch {
        if (!cancelled) setSampleApp(null);
      } finally {
        if (!cancelled) setSampleLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sampleUid]);

  const resolvedSubject = sampleApp ? resolveMergeFields(subject, sampleApp) : subject;
  const resolvedBodyHtml = sampleApp ? resolveMergeFields(composer.getSendHtml(), sampleApp) : composer.getSendHtml();
  const showPreview = !result && (subject.trim() !== '' || !composer.isEmpty);

  const handleAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setAttachError('Attachment must be under 5MB');
      return;
    }
    setAttachError('');
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result || '').split(',')[1] || '';
      if (base64) {
        setAttachments((prev) => [...prev, { name: file.name, base64Content: base64, mimeType: file.type || 'application/octet-stream' }]);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSend = async () => {
    if (!subject.trim() || composer.isEmpty) {
      notifyToast('warning', 'Subject and body are required');
      return;
    }
    setSending(true);
    setSendError('');
    try {
      const data: any = await safeFetch('/api/admin/workflow/bulk-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map((i) => ({ uid: i.uid, applicationId: i.applicationId })),
          nudgeType,
          subject: subject.trim(),
          htmlBody: composer.getSendHtml(),
          ...(attachments.length > 0 ? { attachments } : {}),
          ...(documentTemplateIds.length > 0 ? { documentTemplateIds } : {}),
          idempotencyKey,
        }),
      });
      setOutcomes(Array.isArray(data?.outcomes) ? data.outcomes : null);
      setResult({
        jobId: data.jobId,
        status: data.status || 'queued',
        recipientCount: data.recipientCount ?? 0,
        deduplicated: data.deduplicated === true,
      });
      notifyToast(
        'success',
        data.deduplicated
          ? 'Duplicate send suppressed — this email was already queued.'
          : `Status email queued for ${data.recipientCount ?? 0} applicant${(data.recipientCount ?? 0) === 1 ? '' : 's'}`,
      );
      onSent();
    } catch (err: any) {
      // A 422 carries the per-recipient outcomes — show them inline
      // instead of a bare error so the admin sees exactly who was refused
      // and why. Nothing was queued in that case.
      if (Array.isArray(err?.data?.outcomes)) setOutcomes(err.data.outcomes);
      setSendError(err?.data?.error || err.message || 'Failed to queue the email');
    } finally {
      setSending(false);
    }
  };

  const rejectedOutcomes = (outcomes || []).filter((o) => o.status === 'rejected');
  const itemLabel = (o: BulkEmailOutcome) => {
    const item = items.find((i) => i.uid === o.uid);
    return item?.displayName || item?.email || o.uid;
  };

  return (
    <div className="fixed inset-0 z-[9300] flex items-center justify-center p-4 bg-slate-900/50" role="dialog" aria-modal="true" aria-label="Send status email" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-extrabold text-slate-900">Send status email</h3>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">
              To {items.length} selected applicant{items.length === 1 ? '' : 's'} — each recipient gets their own resolved copy.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close email composer" className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center">
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <div className="px-5 py-4 space-y-3 max-h-[75vh] overflow-y-auto">
            <div className="flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-xl">
              <CheckCircle2 className="h-4 w-4 shrink-0 mt-px" />
              <span>
                {result.deduplicated
                  ? 'This email was already queued — the duplicate was suppressed.'
                  : `Queued for ${result.recipientCount} applicant${result.recipientCount === 1 ? '' : 's'}. Delivery progress:`}
              </span>
            </div>
            {rejectedOutcomes.length > 0 && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 space-y-1.5">
                <p className="font-bold text-amber-800">Not queued ({rejectedOutcomes.length}):</p>
                <ul className="space-y-1">
                  {rejectedOutcomes.map((o) => (
                    <li key={o.uid} className="text-amber-700">
                      <span className="font-semibold">{itemLabel(o)}</span> — {o.reason || 'Could not be queued'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <BroadcastJobProgress jobId={result.jobId} initialStatus={result.status} recipientCount={result.recipientCount} deduplicated={result.deduplicated} />
            <div className="flex justify-end pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2.5 min-h-[44px] rounded-xl bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-bold cursor-pointer">Done</button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 space-y-3 max-h-[75vh] overflow-y-auto">
              <label className="block">
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">Nudge type</span>
                <select value={nudgeType} onChange={(e) => setNudgeType(e.target.value)} className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-800 bg-white">
                  <option value="status_update">Status update</option>
                  <option value="reminder">Reminder</option>
                  <option value="document_request">Document request</option>
                  <option value="payment_reminder">Payment reminder</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">Subject</span>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800" placeholder="Application status update" />
              </label>
              <div>
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">Body</span>
                <div className="mt-1">
                  <ComposerBody
                    composer={composer}
                    minHeight={220}
                    placeholder="Write the message… Use {{first_name}}, {{program_name}} etc. — each recipient's own values are resolved when their copy is sent."
                    fieldPickerSlot={
                      <MergeFieldPicker
                        categories={MERGE_FIELD_CATEGORIES}
                        onInsert={(tag) => composer.insertTextAtCursor(tag)}
                        buttonLabel="Insert Field"
                        align="right"
                      />
                    }
                  />
                </div>
                {composer.placeholders.length > 0 && (
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    Dynamic fields in this message:{' '}
                    {composer.placeholders.map((ph) => (
                      <code key={ph} className="text-purple-700 bg-purple-50 px-1 rounded mr-1 font-mono">{ph}</code>
                    ))}
                  </p>
                )}
              </div>

              {/* Preview with merge fields resolved for a sample recipient */}
              {showPreview && (
                <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5">
                      <Eye className="h-3.5 w-3.5 text-amber-600" />
                      <span className="text-[11px] font-bold text-amber-700 uppercase tracking-wide">Preview</span>
                    </span>
                    <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                      Resolved for
                      <select
                        aria-label="Preview sample recipient"
                        value={sampleItem?.uid || ''}
                        onChange={(e) => setSampleUid(e.target.value)}
                        className="border border-amber-200 rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-700 bg-white max-w-[220px]"
                      >
                        {items.map((i) => (
                          <option key={i.uid} value={i.uid}>
                            {i.displayName || i.email || i.uid}
                          </option>
                        ))}
                      </select>
                      <span className="text-amber-600 font-bold">(sample recipient)</span>
                    </label>
                    {sampleLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                  </div>
                  <div className="bg-white border border-amber-200 rounded-lg p-3 text-xs text-slate-700 max-h-64 overflow-y-auto space-y-2">
                    <p className="whitespace-pre-wrap">
                      <span className="font-bold text-slate-500">Subject: </span>{resolvedSubject}
                    </p>
                    <div
                      className="prose prose-sm max-w-none text-xs text-slate-800 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(resolvedBodyHtml) }}
                    />
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Every recipient receives their own copy with their own values resolved, and any documents below are generated from their own application.
                  </p>
                </div>
              )}

              {/* Templates, attachments, document templates */}
              <div className="border-t border-slate-100 pt-3 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => setShowTemplatePicker(true)} className="flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold rounded-lg transition-all cursor-pointer">
                    <FileText className="h-3.5 w-3.5" />
                    <span>Load Template</span>
                  </button>
                  <label className="flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer shadow-sm">
                    <Upload className="h-3.5 w-3.5 text-brand" />
                    <span>Upload File</span>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleAttachment} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx" />
                  </label>
                  <span className="text-xs text-slate-500">Max 5MB per file</span>
                </div>
                {attachError && <p className="text-xs font-semibold text-rose-600">{attachError}</p>}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((a, i) => (
                      <span key={`${a.name}-${i}`} className="flex items-center space-x-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-lg text-xs font-bold text-brand">
                        <Paperclip className="h-3 w-3" />
                        <span className="truncate max-w-[150px]">{a.name}</span>
                        <button type="button" aria-label={`Remove ${a.name}`} onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))} className="ml-1 text-blue-400 hover:text-rose-600 cursor-pointer"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
                <DocumentTemplateMultiSelect
                  value={documentTemplateIds}
                  onChange={setDocumentTemplateIds}
                  label="Generate & Attach Documents"
                  hint="Generated from each recipient's own application when their email is sent, saved to their Generated Documents, and attached as PDFs. If a document fails to generate for a recipient, that recipient's email is not sent."
                />
              </div>

              {sendError && (
                <div role="alert" className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-px" /><span>{sendError}</span>
                </div>
              )}
              {rejectedOutcomes.length > 0 && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 space-y-1.5">
                  <p className="font-bold text-amber-800">Could not be queued ({rejectedOutcomes.length}):</p>
                  <ul className="space-y-1">
                    {rejectedOutcomes.map((o) => (
                      <li key={o.uid} className="text-amber-700">
                        <span className="font-semibold">{itemLabel(o)}</span> — {o.reason || 'Could not be queued'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2.5 min-h-[44px] rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 cursor-pointer">Cancel</button>
              <button type="button" onClick={handleSend} disabled={sending} className="px-4 py-2.5 min-h-[44px] rounded-xl bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
              </button>
            </div>
          </>
        )}
      </div>
      {showTemplatePicker && (
        <TemplatePickerModal
          onSelect={(tmpl) => {
            // Subject and documentTemplateIds are forwarded by the
            // composer's onTemplateSubject / onTemplateDocumentTemplates hooks.
            composer.insertTemplate({ name: tmpl.name, subject: tmpl.subject, htmlBody: tmpl.htmlBody, documentTemplateIds: tmpl.documentTemplateIds });
            if (tmpl.attachments?.length) setAttachments(tmpl.attachments);
            setShowTemplatePicker(false);
          }}
          onClose={() => setShowTemplatePicker(false)}
        />
      )}
    </div>
  );
}

export const ApplicantDrawer: React.FC<ApplicantDrawerProps> = ({ uid, uids = [], onClose, onNavigate, onViewApplicant, onChanged, isSeniorAdmin = true }) => {
  const [data, setData] = useState<ApplicantFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  // Freshness bookkeeping (PR-20): when the displayed data was loaded,
  // which uid it belongs to, and a tick bumped per successful load so
  // child panels (Admin Actions) refetch alongside the drawer.
  const [lastLoadedAt, setLastLoadedAt] = useState<number | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // Optimistic stage move (drawer responsiveness fix): the stage the
  // admin has CONFIRMED a move to, shown immediately by the stepper,
  // the current-stage caption and the phase panel while the server
  // processes the move. Display only — `data` (the server truth) is
  // never written optimistically. Cleared when the server confirms
  // (a load lands carrying this stage), when a full reconcile load
  // lands with any stage, or immediately when the move is refused
  // (rollback — the mutation's error toast carries the server's
  // reason).
  const [pendingMoveStage, setPendingMoveStage] = useState<string | null>(null);
  const pendingMoveStageRef = useRef<string | null>(null);
  const beginOptimisticMove = useCallback((stage: string | null | undefined) => {
    if (!stage) return;
    pendingMoveStageRef.current = stage;
    setPendingMoveStage(stage);
  }, []);
  const endOptimisticMove = useCallback(() => {
    pendingMoveStageRef.current = null;
    setPendingMoveStage(null);
  }, []);
  const loadSeqRef = useRef(0);
  const loadedUidRef = useRef<string | null>(null);
  const lastLoadedRef = useRef(0);
  const [activeTab, setActiveTab] = useState<'gates' | 'details' | 'operations' | 'activity'>('gates');
  const [gatesSubFilter, setGatesSubFilter] = useState<'all' | 'applicant_gates' | 'admin_tasks' | 'internal_tasks' | 'automations'>('all');
  const [copied, setCopied] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  // Approve-with-unmet-gates (2026-10-12): the server's 409 UNMET_GATES
  // payload, driving the guided approve+override dialog.
  const [approveBlock, setApproveBlock] = useState<UnmetGate[] | null>(null);
  const [showDemote, setShowDemote] = useState(false);
  const [demoteTargetStage, setDemoteTargetStage] = useState<string | undefined>(undefined);
  const [showNudge, setShowNudge] = useState(false);
  const [showAiDraft, setShowAiDraft] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [assignAutoOpen, setAssignAutoOpen] = useState(false);
  const [sendingStageNudge, setSendingStageNudge] = useState(false);
  const [preNotes, setPreNotes] = useState('');

  const handleTriggerStageNudge = async () => {
    const targetAppId = record?.applicationId || (record as any)?.id || uid;
    if (!targetAppId) return;
    setSendingStageNudge(true);
    try {
      const res = await safeFetch(`/api/admin/workflow/applications/${encodeURIComponent(targetAppId)}/trigger-stage-nudge`, {
        method: 'POST',
      });
      if (res.unconfigured || res.success === false) {
        notifyToast('warning', res.message || 'Stage nudge is not configured for this stage.');
      } else {
        notifyToast('success', res.message || 'Stage nudge email sent successfully.');
        if (uid) load(uid);
        onChanged();
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to trigger stage nudge.');
    } finally {
      setSendingStageNudge(false);
    }
  };
  const [acting, setActing] = useState<string | null>(null);
  const [extDays, setExtDays] = useState('');
  const [extReason, setExtReason] = useState('');
  const drawerRef = useRef<HTMLDivElement>(null);
  const blockersRef = useRef<HTMLDivElement>(null);
  const focusSectionRef = useRef<HTMLDivElement>(null);
  const gatePanelRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  const load = useCallback(async (targetUid: string, opts?: { light?: boolean }) => {
    // Sequence guard (PR-20): a slow /full for applicant A must never
    // paint over applicant B after a J/K or click switch — only the
    // most recent request may touch state.
    const seq = ++loadSeqRef.current;
    try {
      setLoading(true);
      setError(null);
      setErrorCode(null);
      // Light reads (background poll / focus return only) ask the
      // server to serve the persisted record without a snapshot
      // recompute when it is already at the current rules version.
      // Opens and post-mutation reloads stay full recompute-first.
      const fullUrl = `/api/admin/workflow/applicants/${encodeURIComponent(targetUid)}/full`;
      const d: any = await safeFetch(opts?.light ? `${fullUrl}?light=1` : fullUrl);
      // P0-1a: the /full endpoint now decrypts the application payload
      // SERVER-side before responding (the client no longer holds the
      // DEK). PiiText below still guards every PII render site from
      // rendering a raw object (React error #31).
      if (seq !== loadSeqRef.current) return; // superseded by a newer load
      loadedUidRef.current = targetUid;
      lastLoadedRef.current = Date.now();
      setData(d as ApplicantFull);
      setLastLoadedAt(lastLoadedRef.current);
      setRefreshTick((t) => t + 1);
      // Reconcile an optimistic stage move: a FULL load is the
      // post-mutation truth, so whatever stage it carries replaces
      // the optimistic display (the effect below also clears on an
      // exact match, possibly via a light poll that already saw the
      // committed move). Light loads never clear it early — the
      // server may simply not have committed the move yet.
      if (!opts?.light && pendingMoveStageRef.current) {
        pendingMoveStageRef.current = null;
        setPendingMoveStage(null);
      }
    } catch (err: any) {
      if (seq !== loadSeqRef.current) return;
      setError(err.message || 'Failed to load applicant');
      setErrorCode(err?.data?.code || null);
      // PR-20: a failed BACKGROUND refresh must not blank a drawer that
      // already shows this applicant — keep the last good data and let
      // the banner explain it's stale. First-load failures (nothing
      // loaded for this uid) still clear to the error state.
      if (loadedUidRef.current !== targetUid) {
        setData(null);
      }
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (uid) {
      // Full per-applicant reset (PR-20): decision notes, deadline form,
      // modal flags and copy state from the PREVIOUS applicant must
      // never bleed into the next one.
      setData(null);
      loadedUidRef.current = null;
      lastLoadedRef.current = 0;
      setLastLoadedAt(null);
      setPreNotes('');
      setExtDays('');
      setExtReason('');
      setShowOverride(false);
      setShowDemote(false);
      setShowNudge(false);
      setShowAiDraft(false);
      setShowCreateTask(false);
      setApproveBlock(null);
      setCopied(false);
      endOptimisticMove();
      load(uid);
    }
  }, [uid, load, endOptimisticMove]);

  // External-change freshness (PR-20): when the admin returns to this
  // tab/window, refetch so changes made by the applicant or another
  // admin surface without a manual page reload. Throttled to one
  // refetch per 10s; the drawer's own writes already reload eagerly.
  // Honest guarantee: changes appear on focus/visibility return (and on
  // demand via the header refresh button), not via a live subscription.
  useEffect(() => {
    if (!uid) return;
    const maybeRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadedRef.current < 10000) return;
      load(uid, { light: true });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', maybeRefresh);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', maybeRefresh);
    };
  }, [uid, load]);

  // Optimistic move confirmation: as soon as ANY load (light polls
  // included) shows the server record already at the pending stage,
  // the move is confirmed — drop the optimistic overlay and let the
  // server data speak for itself.
  useEffect(() => {
    if (pendingMoveStage && data?.record?.stage === pendingMoveStage) {
      endOptimisticMove();
    }
  }, [data, pendingMoveStage, endOptimisticMove]);

  // Instant updates while the drawer is open (2026-10-16): the codebase
  // idiom for admin workflow data is REST + disciplined refetch (no
  // realtime listeners on these collections anywhere in the admin
  // shell), so the drawer polls on a slow interval while it is open
  // AND visible. Another admin's change, an automation event, or an
  // applicant upload then appears without closing/reopening. The 15s
  // floor keeps the poll from doubling the focus refresh; load()'s
  // sequence guard makes overlapping loads safe; the interval is
  // cleared on close / applicant switch (no listener leaks).
  useEffect(() => {
    if (!uid) return;
    const iv = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadedRef.current < 15000) return;
      load(uid, { light: true });
    }, 20000);
    return () => clearInterval(iv);
  }, [uid, load]);

  const [closing, setClosing] = useState(false);
  const requestClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => {
      onClose();
    }, 200);
  }, [closing, onClose]);

  // Esc closes; J/K navigate within the provided list context.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'Escape') requestClose();
      else if ((e.key === 'j' || e.key === 'k') && uids.length > 0 && uid && onNavigate) {
        const i = uids.indexOf(uid);
        const next = e.key === 'j' ? uids[(i + 1 + uids.length) % uids.length] : uids[(i - 1 + uids.length) % uids.length];
        if (next && next !== uid) onNavigate(next);
      }
    };
    window.addEventListener('keydown', onKey);
    drawerRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [uid, uids, onNavigate, requestClose]);

  if (!uid) return null;

  const record: FullApplicantRecord | undefined = data?.record;
  const application = data?.application;

  const stageIdx = record ? STAGE_ORDER.indexOf(record.stage as any) : -1;

  // Stage DISPLAY index: while a confirmed move is in flight this is
  // the optimistic target, so the stepper and the current-stage
  // caption move instantly. All logic (gates, advance eligibility,
  // handlers) keeps using the server stageIdx above.
  const displayStageIdx = pendingMoveStage ? STAGE_ORDER.indexOf(pendingMoveStage as any) : stageIdx;

  const nextStageLabel = stageIdx >= 0 && stageIdx < STAGE_ORDER.length - 1
    ? STAGE_LABELS[STAGE_ORDER[stageIdx + 1]]
    : null;

  const copyAppId = async () => {
    try {
      await navigator.clipboard.writeText(record?.applicationId || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const mutate = async (label: string, fn: () => Promise<any>, onOk?: (d: any) => void, opts?: { optimisticStage?: string }) => {
    // Optimistic stage display: the stepper, caption and phase panel
    // show the target stage from the moment the admin confirms, while
    // the POST is still in flight. Server truth still comes from the
    // post-mutation load(); a refusal rolls the display back here.
    if (opts?.optimisticStage) beginOptimisticMove(opts.optimisticStage);
    try {
      setActing(label);
      const d = await fn();
      notifyToast('success', label === 'advancing' ? 'Applicant advanced' : 'Done');
      onOk?.(d);
      onChanged();
      if (uid) load(uid);
    } catch (err: any) {
      // Rollback: the server refused (gate block, validation) — the
      // display returns to the true stage and the toast carries the
      // server's own reason.
      if (opts?.optimisticStage) endOptimisticMove();
      notifyToast('error', err.message || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  const handleAdvance = (closure?: { closureType: string; closureReason?: string }) => {
    const idx = record ? STAGE_ORDER.indexOf(record.stage as any) : -1;
    const nextStage = idx >= 0 && idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1] : undefined;
    return mutate('advancing', () =>
      safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/advance`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(closure || {}) }),
      undefined,
      { optimisticStage: nextStage });
  };

  // Pinned footer advance (2026-10-14): the same action the gate panel
  // renders, without scrolling to it. Closure from preDeparture requires
  // an explicit closure type that only the gate panel's selector holds —
  // never auto-assumed — so at that stage the pinned action brings the
  // panel (and its selector) into view instead of firing the advance.
  const handlePinnedAdvance = () => {
    if (record?.stage === 'preDeparture') {
      gatePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    handleAdvance();
  };

  // Stage moves (promote / demote) live on the header stepper
  // (handleStageClick in the render below): current stage → gates tab,
  // earlier stage → DemoteModal pre-filled with that stage, future
  // stage → direct advance when it is the ready next stage, otherwise
  // the OverrideModal for senior admins. There is deliberately no
  // second promote/demote surface inside the tabs.

  const handleClearOverride = () => mutate('clearing override', () =>
    safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/clear-override`, { method: 'POST' }));

  const handleClearDemotion = () => mutate('clearing demotion', () =>
    safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/clear-demotion`, { method: 'POST' }));

  const handleRecompute = () => mutate('recomputing', () =>
    safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/recompute`, { method: 'POST' }));

  // PR-M: persistent clearance of a POTENTIAL redlist match (and its
  // revocation). The server recomputes after each write; mutate's
  // refresh then re-renders the gate area in its new state.
  const handleClearRedlist = (note: string) => mutate('clearing redlist match', () =>
    safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/redlist-clearance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    }));

  const handleRevokeRedlist = () => mutate('revoking redlist clearance', () =>
    safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/redlist-clearance/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }));

  const handlePreApproval = async (decision: 'approve' | 'return' | 'reject' | 'hold') => {
    if (decision !== 'approve' && !preNotes.trim()) {
      notifyToast('warning', 'Notes are required for this decision');
      return;
    }
    if (decision !== 'approve') {
      mutate(`pre-approval: ${decision}`, () =>
        safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/pre-approval`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ decision, notes: preNotes.trim() || undefined }),
        }));
      return;
    }
    // Approve (2026-10-12): if pre-vetting gates are unmet, the server
    // answers 409 UNMET_GATES listing them — turn that into the guided
    // approve+override prompt instead of a dead-end error toast.
    // Approving moves the applicant one stage forward — show that
    // stage immediately (optimistic display), and roll back if the
    // server refuses (including the UNMET_GATES prompt path: no move
    // has happened until the guided override actually succeeds).
    const idx = record ? STAGE_ORDER.indexOf(record.stage as any) : -1;
    const nextStage = idx >= 0 && idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1] : undefined;
    beginOptimisticMove(nextStage);
    try {
      setActing('pre-approval: approve');
      await safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/pre-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, notes: preNotes.trim() || undefined }),
      });
      notifyToast('success', 'Done');
      onChanged();
      if (uid) load(uid);
    } catch (err: any) {
      endOptimisticMove();
      if (err?.data?.code === 'UNMET_GATES' && Array.isArray(err.data.unmetGates)) {
        setApproveBlock(err.data.unmetGates as UnmetGate[]);
      } else {
        notifyToast('error', err.message || 'Action failed');
      }
    } finally {
      setActing(null);
    }
  };

  const handlePauseEmails = (paused: boolean) => mutate(paused ? 'pausing emails' : 'resuming emails', () =>
    safeFetch('/api/admin/workflow/pause', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ level: 'application', uid, paused }),
    }));

  const handleExtendDeadline = () => {
    const days = parseInt(extDays, 10);
    if (!days || days <= 0) {
      notifyToast('warning', 'Enter a number of days');
      return;
    }
    mutate('extending deadline', () =>
      safeFetch(`/api/admin/workflow/applicants/${encodeURIComponent(uid)}/extend-deadline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days, reason: extReason.trim() || undefined }),
      }));
  };

  const blockerCount = record?.blockers?.length || 0;
  const isBlocked = blockerCount > 0;
  const isReady = !!record?.readyToAdvance;
  // Pre-claim candidate (PR-20): board-synthesized entry, no user
  // account or workflow record yet — details are read-only and the
  // stage machinery (gates, actions, focus forms) doesn't apply.
  const isUnclaimed = record?.unclaimed === true;

  const deadline = application?.applicationDeadline;
  const daysLeft = deadline ? Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000) : null;

  const invoices: ApplicantFull['invoices'] = data?.invoices || [];
  // Canonical paid field is `amountPaid`; `paidAmount` is a legacy alias.
  const outstanding = invoices.reduce((acc, inv) => acc + Math.max(0, (inv.total || 0) - Number(inv.paidAmount ?? inv.amountPaid ?? 0)), 0);

  const visaAttempts = (application as any)?.visaAttempts || [];
  const embassyAppointment = (application as any)?.embassyAppointment || (application as any)?.visaDetails?.embassyAppointmentAt;
  const flightDetails = (application as any)?.flightDetails || (application as any)?.itinerary;
  // Documents come from the /full payload (subcollection + embedded array,
  // merged server-side); fall back to the embedded array alone.
  const documents = data?.documents?.length ? data.documents : ((application as any)?.documents || []);
  const emergencyContacts = application?.emergencyContacts || (application as any)?.personalDetails?.emergencyContacts || [];
  const placements = application?.placements?.length ? application.placements : ((application as any)?.placement ? [(application as any).placement] : []);
  const education = (application as any)?.educationalBackground || (application as any)?.education || (application as any)?.educationHistory || [];
  const workHistory = (application as any)?.workExperience || (application as any)?.workHistory || (application as any)?.experience || [];
  const photoUrl = resolvePhotoUrl(application);

  const [drawerWidth, setDrawerWidth] = useState<number>(640);
  const [isResizing, setIsResizing] = useState<boolean>(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const newWidth = window.innerWidth - e.clientX;
        const maxWidth = Math.min(1400, window.innerWidth * 0.92);
        const clamped = Math.max(480, Math.min(maxWidth, newWidth));
        setDrawerWidth(clamped);
      });
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <div className="fixed inset-0 z-[9000]" role="dialog" aria-modal="true" aria-label={`Applicant detail${record ? `: ${record.displayName}` : ''}`}>
      <div className={`absolute inset-0 bg-slate-900/50 backdrop-blur-[2px] ${closing ? 'portal-backdrop-out' : 'portal-backdrop-in'}`} onClick={requestClose} />
      <div
        ref={drawerRef}
        tabIndex={-1}
        className={`absolute right-0 top-0 h-full bg-slate-50 shadow-2xl flex flex-col outline-none max-w-[95vw] ${closing ? 'portal-drawer-right-out' : 'portal-drawer-right-in'} ${isResizing ? 'select-none' : ''}`}
        style={{ width: `${drawerWidth}px` }}
      >
        {/* Drag handle to widen/narrow drawer */}
        <div
          onMouseDown={handleMouseDown}
          title="Drag left to widen drawer"
          className={`absolute left-0 top-0 bottom-0 w-2.5 cursor-ew-resize hover:bg-brand-primary-dark/40 active:bg-brand-primary transition-colors z-50 group flex items-center justify-center ${isResizing ? 'bg-brand-primary' : ''}`}
        >
          <div className="w-1 h-8 bg-slate-400/60 group-hover:bg-white rounded-full transition-colors" />
        </div>
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-5 py-4 shrink-0">
          <div className="flex items-start gap-3">
            {/* Keyed per applicant+photo so a failed image on one applicant
                doesn't suppress the next applicant's photo. */}
            <DrawerAvatar key={`${uid}:${photoUrl}`} photoUrl={photoUrl} name={record?.displayName || ''} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-extrabold text-slate-900 tracking-tight truncate">{loading && !data ? 'Loading…' : record?.displayName || '—'}</h2>
                {loading && data && (
                  <span className="text-[10px] font-bold text-slate-400 bg-slate-100 rounded-full px-2 py-0.5 whitespace-nowrap animate-pulse">
                    Refreshing…
                  </span>
                )}
                {uids.length > 1 && (
                  <span className="text-[10px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                    {uids.indexOf(uid) + 1} / {uids.length} · J/K
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <button
                  type="button"
                  onClick={copyAppId}
                  title="Copy application ID"
                  className="font-mono text-[11px] text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md px-1.5 py-0.5 flex items-center gap-1 cursor-pointer"
                >
                  #{record?.applicationId || '—'}
                  {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                </button>
                <span className="text-[11px] font-semibold text-slate-500">{record?.program || ''}{record?.season ? ` · ${record.season}` : ''}</span>
                {lastLoadedAt && (
                  <span className="text-[11px] font-medium text-slate-400" title="When this data was last loaded from the server">
                    · Updated {fmtClock(lastLoadedAt)}
                  </span>
                )}
              </div>
              {record && (
                <div className="mt-2">
                  <StatusSignal record={record} compact />
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => uid && load(uid)}
              disabled={loading}
              aria-label="Refresh applicant data"
              title="Refresh applicant data"
              className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0 disabled:opacity-50"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button type="button" onClick={requestClose} aria-label="Close applicant drawer" className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Applicant actions: one tidy row under the identity block —
              the stage nudge for the current stage and the automation
              assignment entry point. Refresh and Close stay up with the
              identity row. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleTriggerStageNudge}
              disabled={sendingStageNudge || isUnclaimed}
              title="Send configured stage nudge email for applicant's current program and stage"
              className="flex items-center gap-1.5 px-2.5 py-1.5 border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-[11px] font-bold shrink-0 cursor-pointer disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5 text-blue-600" />
              {sendingStageNudge ? 'Sending...' : 'Stage Nudge'}
            </button>
            <button
              type="button"
              onClick={() => setAssignAutoOpen(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-[11px] font-bold shrink-0 cursor-pointer"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Assign Flow
            </button>
          </div>

          {/* 8-stage stepper (interactive) — the drawer's native
              promote/demote path: select a stage to demote back to it,
              advance to a ready next stage, or open the override flow.
              The caption names the current stage so the admin always
              knows where this applicant stands. */}
          {record && (
            <div className="mt-4">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Workflow stage</p>
                <p className="text-[11px] text-slate-500 text-right">
                  Current stage: <span className="font-bold text-slate-800">{displayStageIdx >= 0 ? STAGE_LABELS[STAGE_ORDER[displayStageIdx]] : '—'}</span>
                  {pendingMoveStage ? (
                    <span className="inline-flex items-center gap-1 ml-1.5 font-bold text-indigo-600" role="status">
                      <Loader2 className="h-3 w-3 animate-spin" /> Moving…
                    </span>
                  ) : (
                    !isUnclaimed && <span className="hidden sm:inline"> · Select a stage to move the applicant</span>
                  )}
                </p>
              </div>
              <div className="mt-2 flex items-center" aria-label="Workflow stages">
              {STAGE_ORDER.map((key, i) => {
                const done = displayStageIdx >= 0 && i < displayStageIdx;
                const current = i === displayStageIdx;
                const isFuture = displayStageIdx >= 0 && i > displayStageIdx;

                const handleStageClick = () => {
                  if (isUnclaimed) return;
                  // A confirmed move is already in flight — the stepper
                  // is showing its target; ignore further stage clicks
                  // until the server reconciles.
                  if (pendingMoveStage) return;
                  if (current) {
                    setActiveTab('gates');
                    gatePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } else if (done) {
                    // Clicking an earlier stage opens demote modal pre-filled with that target stage
                    setDemoteTargetStage(key);
                    setShowDemote(true);
                  } else if (isFuture) {
                    setActiveTab('gates');
                    if (i === stageIdx + 1 && record.readyToAdvance && !record.overridden) {
                      handleAdvance();
                    } else if (isSeniorAdmin) {
                      setShowOverride(true);
                    } else {
                      notifyToast('warning', 'Only senior admins can override gates to advance.');
                    }
                  }
                };

                return (
                  <React.Fragment key={key}>
                    <button
                      type="button"
                      onClick={handleStageClick}
                      disabled={isUnclaimed}
                      title={
                        current
                          ? `${STAGE_LABELS[key]} (Current stage)`
                          : done
                            ? `Click to demote applicant back to ${STAGE_LABELS[key]}`
                            : `Click to promote / override to ${STAGE_LABELS[key]}`
                      }
                      className="flex flex-col items-center shrink-0 cursor-pointer group focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    >
                      <div
                        className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-extrabold border-2 transition-transform group-hover:scale-110 ${
                          done
                            ? 'bg-emerald-500 border-emerald-500 text-white group-hover:bg-amber-600 group-hover:border-amber-600'
                            : current
                              ? record.overridden
                                ? 'bg-[var(--color-override-bg)] border-[var(--color-override)] text-[var(--color-override)] ring-2 ring-[var(--color-override-border)]'
                                : 'bg-white border-blue-500 text-blue-600 ring-2 ring-blue-100'
                              : 'bg-white border-slate-200 text-slate-400 group-hover:border-purple-400 group-hover:text-purple-600'
                        }`}
                        aria-current={current ? 'step' : undefined}
                        aria-label={`${STAGE_LABELS[key]}${done ? ' (completed)' : current ? ' (current)' : ''}`}
                      >
                        {done ? <Check className="h-3.5 w-3.5 group-hover:hidden" /> : i + 1}
                        {done && <ChevronLeft className="h-3.5 w-3.5 hidden group-hover:block" />}
                      </div>
                      <span className={`mt-1 text-[11px] font-bold uppercase tracking-wide max-w-[64px] text-center leading-tight ${current ? 'text-slate-900 font-extrabold' : 'text-slate-400 group-hover:text-slate-700'}`}>
                        {STAGE_LABELS[key]}
                      </span>
                    </button>
                    {i < STAGE_ORDER.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-0.5 mb-5 ${i < stageIdx ? 'bg-emerald-400' : 'bg-slate-200'}`} aria-hidden="true" />
                    )}
                  </React.Fragment>
                );
              })}
              </div>
            </div>
          )}
        </div>

        {/* Tab Menu Bar */}
        {record && (
          <div className="bg-slate-100/90 border-b border-slate-200/80 px-5 pt-1.5 shrink-0 flex items-center justify-between gap-1 overflow-x-auto text-xs font-bold">
            <div className="flex items-center gap-1 min-w-max">
              <button
                type="button"
                onClick={() => setActiveTab('gates')}
                className={`px-3 py-2 rounded-t-xl border-b-2 transition-colors cursor-pointer flex items-center gap-1.5 ${
                  activeTab === 'gates'
                    ? 'border-indigo-600 bg-white text-indigo-950 shadow-xs'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/60'
                }`}
              >
                <span>Gates & Tasks</span>
                {(blockerCount > 0) && (
                  <span className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.2 rounded-full font-extrabold">
                    {blockerCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('details')}
                className={`px-3 py-2 rounded-t-xl border-b-2 transition-colors cursor-pointer flex items-center gap-1.5 ${
                  activeTab === 'details'
                    ? 'border-indigo-600 bg-white text-indigo-950 shadow-xs'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/60'
                }`}
              >
                <span>Profile & Details</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('operations')}
                className={`px-3 py-2 rounded-t-xl border-b-2 transition-colors cursor-pointer flex items-center gap-1.5 ${
                  activeTab === 'operations'
                    ? 'border-indigo-600 bg-white text-indigo-950 shadow-xs'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/60'
                }`}
              >
                <span>Operations & Finance</span>
                {outstanding > 0 && (
                  <span className="bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.2 rounded-full font-extrabold">
                    Due
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('activity')}
                className={`px-3 py-2 rounded-t-xl border-b-2 transition-colors cursor-pointer flex items-center gap-1.5 ${
                  activeTab === 'activity'
                    ? 'border-indigo-600 bg-white text-indigo-950 shadow-xs'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/60'
                }`}
              >
                <span>Activity & Email</span>
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4">
          {/* FIX (2026-10-11): full skeleton only on first load. During
              background reloads (e.g. after a screening record is saved) the
              existing content stays mounted — previously the skeleton replaced
              everything, unmounting embedded tabs and causing remount loops. */}
          {loading && !data && (
            <div className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-slate-200/80 rounded-2xl h-28 animate-pulse" />
              ))}
            </div>
          )}
          {error && errorCode === 'CROSS_WORKSPACE' && !data && (
            <div className="bg-sky-50 border border-sky-200 rounded-2xl px-4 py-4 flex items-start gap-2.5">
              <ShieldCheck className="h-5 w-5 text-sky-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-extrabold text-sky-900">Different workspace</p>
                <p className="text-xs font-medium text-sky-800 mt-1">{error}</p>
                <button type="button" onClick={onClose} className="mt-2.5 px-3.5 py-2 min-h-[44px] rounded-xl bg-sky-700 hover:bg-sky-600 text-white text-xs font-bold cursor-pointer">Close drawer</button>
              </div>
            </div>
          )}
          {error && !(errorCode === 'CROSS_WORKSPACE' && !data) && (
            <div className={`${data ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'} border rounded-2xl px-4 py-3 flex items-start gap-2`}>
              <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${data ? 'text-amber-600' : 'text-red-600'}`} />
              <div className="flex-1">
                <p className={`text-xs font-bold ${data ? 'text-amber-800' : 'text-red-800'}`}>
                  {data ? `Couldn't refresh — showing the data loaded at ${fmtClock(lastLoadedAt)}. ${error}` : error}
                </p>
                <button type="button" onClick={() => uid && load(uid)} className={`mt-1 text-xs font-bold underline cursor-pointer ${data ? 'text-amber-700' : 'text-red-700'}`}>Retry</button>
              </div>
            </div>
          )}

          {record && data && (
            <>
              {isUnclaimed && (
                <div className="bg-sky-50 border border-sky-200 rounded-2xl px-4 py-3 flex items-start gap-2.5">
                  <UserIcon className="h-4 w-4 text-sky-700 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-extrabold text-sky-900">Awaiting signup — read-only preview</p>
                    <p className="text-xs font-medium text-sky-800 mt-0.5">
                      This candidate was added by a referrer and hasn't claimed their account yet. The details below come
                      from their draft application; stage actions become available once they sign up and claim it.
                    </p>
                  </div>
                </div>
              )}

              {/* TAB 1: GATES & TASKS */}
              {activeTab === 'gates' && (
                <div className="space-y-4 animate-fade-in">
                  {/* Sub-filter chips */}
                  <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-[11px] font-extrabold text-slate-600">
                    <button
                      type="button"
                      onClick={() => setGatesSubFilter('all')}
                      className={`px-2.5 py-1 rounded-lg border transition-colors cursor-pointer shrink-0 ${
                        gatesSubFilter === 'all'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setGatesSubFilter('applicant_gates')}
                      className={`px-2.5 py-1 rounded-lg border transition-colors cursor-pointer shrink-0 ${
                        gatesSubFilter === 'applicant_gates'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                    >
                      Applicant Gates
                    </button>
                    <button
                      type="button"
                      onClick={() => setGatesSubFilter('admin_tasks')}
                      className={`px-2.5 py-1 rounded-lg border transition-colors cursor-pointer shrink-0 ${
                        gatesSubFilter === 'admin_tasks'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                    >
                      Admin Phase Tasks
                    </button>
                    <button
                      type="button"
                      onClick={() => setGatesSubFilter('internal_tasks')}
                      className={`px-2.5 py-1 rounded-lg border transition-colors cursor-pointer shrink-0 ${
                        gatesSubFilter === 'internal_tasks'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                    >
                      Internal Tasks
                    </button>
                    <button
                      type="button"
                      onClick={() => setGatesSubFilter('automations')}
                      className={`px-2.5 py-1 rounded-lg border transition-colors cursor-pointer shrink-0 ${
                        gatesSubFilter === 'automations'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                    >
                      Automations
                    </button>
                  </div>

                  {/* A. Applicant Gates Panel */}
                  {(gatesSubFilter === 'all' || gatesSubFilter === 'applicant_gates') && !isUnclaimed && (
                    <div ref={gatePanelRef} className="scroll-mt-4">
                      <StageGatePanel
                        stage={record.stage}
                        applicantName={record.displayName}
                        prerequisites={record.stagePrerequisites || []}
                        readyToAdvance={record.readyToAdvance}
                        overridden={record.overridden}
                        demoted={record.demoted}
                        demoteReason={record.demoteReason}
                        isSeniorAdmin={isSeniorAdmin}
                        advancing={acting === 'advancing'}
                        onAdvance={handleAdvance}
                        onOverride={() => setShowOverride(true)}
                        onDemote={() => setShowDemote(true)}
                        onDraftEmail={() => setShowAiDraft(true)}
                        onClearRedlist={handleClearRedlist}
                        onRevokeRedlist={handleRevokeRedlist}
                        redlistBusy={acting === 'clearing redlist match' || acting === 'revoking redlist clearance'}
                        actionSlot={
                          record.stage === 'submitted' ? (
                            <div>
                              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">
                                Decision notes <span className="text-slate-400 normal-case font-medium">(required for return / reject / hold)</span>
                              </label>
                              <textarea value={preNotes} onChange={(e) => setPreNotes(e.target.value)} rows={2} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800 resize-y" placeholder="Notes for the applicant…" />
                              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <button type="button" onClick={() => handlePreApproval('approve')} disabled={!!acting} className="px-3 py-2.5 min-h-[44px] rounded-xl bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50 text-white text-xs font-bold cursor-pointer">Approve</button>
                                <button type="button" onClick={() => handlePreApproval('return')} disabled={!!acting} className="px-3 py-2.5 min-h-[44px] rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-xs font-bold cursor-pointer">Return for Corrections</button>
                                <button type="button" onClick={() => handlePreApproval('reject')} disabled={!!acting} className="px-3 py-2.5 min-h-[44px] rounded-xl bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-bold cursor-pointer">Reject</button>
                                <button type="button" onClick={() => handlePreApproval('hold')} disabled={!!acting} className="px-3 py-2.5 min-h-[44px] rounded-xl bg-slate-600 hover:bg-slate-500 disabled:opacity-50 text-white text-xs font-bold cursor-pointer">Place on Hold</button>
                              </div>
                            </div>
                          ) : undefined
                        }
                      />
                    </div>
                  )}

                  {/* A2. Phase Admin Tasks */}
                  {(gatesSubFilter === 'all' || gatesSubFilter === 'admin_tasks') && !isUnclaimed && (
                    <AdminActionGatesPanel
                      applicationId={record.applicationId}
                      currentStage={pendingMoveStage ?? record.stage}
                      stageMovePending={!!pendingMoveStage}
                      applicantName={record.displayName}
                      applicantEmail={record.email}
                      applicantUserId={uid || undefined}
                      refreshSignal={refreshTick}
                      onRefresh={() => { onChanged(); if (uid) load(uid); }}
                    />
                  )}

                  {/* A3. Internal Tasks */}
                  {(gatesSubFilter === 'all' || gatesSubFilter === 'internal_tasks') && !isUnclaimed && (
                    <DrawerTasksSection
                      applicationId={record.applicationId}
                      refreshSignal={refreshTick}
                      onAddTask={() => setShowCreateTask(true)}
                      onChanged={() => { onChanged(); }}
                    />
                  )}

                  {/* A4. Assigned Automations */}
                  {(gatesSubFilter === 'all' || gatesSubFilter === 'automations') && !isUnclaimed && (
                    <DrawerAssignedWorkflows uid={uid} refreshSignal={refreshTick} />
                  )}

                  {/* B + C Stage Machinery */}
                  {gatesSubFilter === 'all' && !isUnclaimed && (
                    <>
                      <ApplicantAdminActions
                        uid={uid}
                        refreshSignal={refreshTick}
                        onDraftEmail={() => setShowAiDraft(true)}
                        onGoToFocus={() => focusSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        onChanged={() => { onChanged(); if (uid) load(uid); }}
                      />
                      <div ref={focusSectionRef} className="scroll-mt-4">
                        <StageFocusContent
                          key={`${uid}:${(application as any)?.applicationId || ''}`}
                          record={record}
                          data={data}
                          onChanged={onChanged}
                          onReload={() => uid && load(uid)}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* TAB 2: PROFILE & DETAILS */}
              {activeTab === 'details' && (
                <div className="space-y-4 animate-fade-in">
                  {/* Stage moves (promote / demote) live on the header stepper
                      above — there is deliberately no second stage list in
                      this tab. This tab is the applicant record itself. */}
                  <Section title="Application details" icon={<UserIcon className="h-4 w-4" />}>
                    {(() => {
                      const pd = (application as any)?.personalDetails || {};
                      const fullName = joinPersonName([pd.firstName, pd.middleName, pd.lastName]) || sanitizeNamePart(pd.fullNameLegal) || sanitizeNamePart(record?.displayName) || '—';
                      const email = pd.email || (application as any)?.userEmail || '—';
                      const phone = pd.phone || pd.mobileNumber || pd.phoneNumber || '—';
                      const addrObj = pd.address && typeof pd.address === 'object' ? pd.address : null;
                      const address = (addrObj
                        ? [addrObj.street, addrObj.apartment, addrObj.city, addrObj.state, addrObj.zip, addrObj.country]
                        : [pd.address, pd.city, pd.state, pd.zip, pd.country]
                      ).filter(Boolean).join(', ') || '—';
                      const dobRaw = pd.dob || pd.dateOfBirth || '';
                      const dobDate = dobRaw ? new Date(dobRaw) : null;
                      const dob = dobDate && !Number.isNaN(dobDate.getTime())
                        ? dobDate.toLocaleDateString(undefined, { dateStyle: 'medium' })
                        : dobRaw;
                      return (
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-3 border-b border-slate-100 pb-3">
                          <div className="col-span-2">
                            <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Full name</dt>
                            <dd className="font-semibold text-slate-800">{fullName}</dd>
                          </div>
                          <div>
                            <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Email</dt>
                            <dd className="font-semibold text-slate-800 break-all">{email}</dd>
                          </div>
                          <div>
                            <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Phone</dt>
                            <dd className="font-semibold text-slate-800">{phone}</dd>
                          </div>
                          {dob && (
                            <div>
                              <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Date of birth</dt>
                              <dd className="font-semibold text-slate-800">{dob}</dd>
                            </div>
                          )}
                          {pd.gender && (
                            <div>
                              <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Gender</dt>
                              <dd className="font-semibold text-slate-800">{pd.gender}</dd>
                            </div>
                          )}
                          {pd.countryOfCitizenship && (
                            <div>
                              <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Citizenship</dt>
                              <dd className="font-semibold text-slate-800">{pd.countryOfCitizenship}</dd>
                            </div>
                          )}
                          {pd.countryOfBirth && (
                            <div>
                              <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Country of birth</dt>
                              <dd className="font-semibold text-slate-800">{pd.countryOfBirth}</dd>
                            </div>
                          )}
                          {pd.maritalStatus && (
                            <div>
                              <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Marital status</dt>
                              <dd className="font-semibold text-slate-800">{pd.maritalStatus}</dd>
                            </div>
                          )}
                          <div className="col-span-2">
                            <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Address</dt>
                            <dd className="font-semibold text-slate-800">{address}</dd>
                          </div>
                        </dl>
                      );
                    })()}
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                      <div>
                        <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Program</dt>
                        <dd className="font-semibold text-slate-800">{record?.program || (application as any)?.programType || '—'}</dd>
                      </div>
                      <div>
                        <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Term</dt>
                        <dd className="font-semibold text-slate-800">{(application as any)?.programTerm || (application as any)?.term || record?.season || '—'}</dd>
                      </div>
                      {((application as any)?.referredByName || (application as any)?.referrerName) && (
                        <div>
                          <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Referrer</dt>
                          <dd className="font-semibold text-slate-800">{(application as any)?.referredByName || (application as any)?.referrerName}</dd>
                        </div>
                      )}
                      {(application as any)?.sponsorName && (
                        <div>
                          <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Sponsor</dt>
                          <dd className="font-semibold text-slate-800">{(application as any)?.sponsorName}</dd>
                        </div>
                      )}
                      {placements.length > 0 && (placements[0] as any)?.employerName && (
                        <div>
                          <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Employer</dt>
                          <dd className="font-semibold text-slate-800">{(placements[0] as any).employerName}</dd>
                        </div>
                      )}
                      <div>
                        <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Deadline</dt>
                        <dd className="font-semibold text-slate-800">{deadline ? new Date(deadline).toLocaleDateString() : '—'}</dd>
                      </div>
                      <div>
                        <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Days remaining</dt>
                        <dd className={`font-semibold ${daysLeft !== null && daysLeft < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                          {daysLeft === null ? '—' : daysLeft < 0 ? `${Math.abs(daysLeft)} days overdue` : `${daysLeft} days`}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Documents</dt>
                        <dd className="font-semibold text-slate-800">
                          {documents.length === 0 ? 'None uploaded' : (
                            <ul className="mt-1 space-y-1">
                              {documents.map((d: any, i: number) => (
                                <li key={d.id || d.docId || i} className="flex items-center justify-between gap-2 font-medium">
                                  {d.downloadUrl ? (
                                    <a href={d.downloadUrl} target="_blank" rel="noreferrer" className="truncate text-blue-700 hover:underline">{d.name || d.type || `Document ${i + 1}`}</a>
                                  ) : (
                                    <span className="truncate">{d.name || d.type || `Document ${i + 1}`}</span>
                                  )}
                                  {d.status && (
                                    <span className={`text-[10px] font-extrabold uppercase tracking-wide rounded-full px-1.5 py-0.5 shrink-0 ${docStatusTone(d.status)}`}>{d.status === 'FurtherReviewRequested' ? 'Further Review Requested' : d.status}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Declaration</dt>
                        <dd className="font-semibold text-slate-800">{(application as any)?.declarationStatus || (application as any)?.declarationSigned ? 'Signed' : '—'}</dd>
                      </div>
                    </dl>
                    {!isUnclaimed && (
                      <div className="mt-3 border-t border-slate-100 pt-3">
                        <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-2">Extend deadline</p>
                        <div className="flex flex-wrap gap-2 items-end">
                          <label className="block">
                            <span className="text-[10px] font-bold text-slate-500">Days</span>
                            <input value={extDays} onChange={(e) => setExtDays(e.target.value)} inputMode="numeric" className="mt-0.5 w-24 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800" placeholder="14" />
                          </label>
                          <label className="block flex-1 min-w-[140px]">
                            <span className="text-[10px] font-bold text-slate-500">Reason</span>
                            <input value={extReason} onChange={(e) => setExtReason(e.target.value)} className="mt-0.5 w-full border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-800" placeholder="Reason for extension" />
                          </label>
                          <button type="button" onClick={handleExtendDeadline} disabled={acting === 'extending deadline'} className="px-3 py-2 min-h-[44px] rounded-xl bg-brand hover:bg-brand-active disabled:opacity-50 text-white text-xs font-bold cursor-pointer">
                            {acting === 'extending deadline' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Extend'}
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-2">Emergency contacts</p>
                      {emergencyContacts.length === 0 ? (
                        <p className="text-xs text-slate-500 font-medium">No emergency contacts on file.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {emergencyContacts.map((c: any, i: number) => (
                            <li key={i} className="text-xs text-slate-700">
                              <span className="font-bold"><PiiText value={c.name ?? c.fullName} /></span>
                              <span className="text-slate-500"> · {c.relationship || ''} · <PiiText value={c.phone ?? c.phoneNumber} />{c.email ? <> · <PiiText value={c.email} /></> : ''}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </Section>

                  <Section title="Education" icon={<GraduationCap className="h-4 w-4" />} defaultOpen={false}>
                    {education.length === 0 ? (
                      <p className="text-xs text-slate-500 font-medium">No education history on file.</p>
                    ) : (
                      <ul className="space-y-2">
                        {education.map((ed: any, i: number) => (
                          <li key={ed.id || i} className="border border-slate-200/80 rounded-xl px-3 py-2 text-xs">
                            <p className="font-bold text-slate-800">
                              {[ed.degreeEarned || ed.degree, ed.fieldOfStudy || ed.field].filter(Boolean).join(' — ') || 'Education'}
                            </p>
                            {(ed.institutionName || ed.institution || ed.school || entryPeriod(ed)) && (
                              <p className="text-slate-500 mt-0.5">
                                {[ed.institutionName || ed.institution || ed.school, entryPeriod(ed)].filter(Boolean).join(' · ')}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Section>

                  <Section title="Work history" icon={<Briefcase className="h-4 w-4" />} defaultOpen={false}>
                    {workHistory.length === 0 ? (
                      <p className="text-xs text-slate-500 font-medium">No work history on file.</p>
                    ) : (
                      <ul className="space-y-2">
                        {workHistory.map((w: any, i: number) => (
                          <li key={w.id || i} className="border border-slate-200/80 rounded-xl px-3 py-2 text-xs">
                            <p className="font-bold text-slate-800">
                              {[w.jobTitle || w.role, w.employerName || w.employer].filter(Boolean).join(' @ ') || 'Work experience'}
                            </p>
                            {(entryPeriod(w) || w.city || w.country) && (
                              <p className="text-slate-500 mt-0.5">
                                {[entryPeriod(w), [w.city, w.country].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Section>

                  {(record.flags?.length || 0) > 0 && (
                    <Section title="Flags" icon={<AlertTriangle className="h-4 w-4" />}>
                      <div className="flex flex-wrap gap-1.5">
                        {record.flags.map((f, i) => (
                          <span key={i} className="text-[10px] font-extrabold uppercase tracking-wide bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{f}</span>
                        ))}
                      </div>
                    </Section>
                  )}
                </div>
              )}

              {/* TAB 3: OPERATIONS & FINANCE */}
              {activeTab === 'operations' && (
                <div className="space-y-4 animate-fade-in">
                  <Section title="Screening" icon={<Stethoscope className="h-4 w-4" />} defaultOpen={true}>
                    {(!data.screenings || data.screenings.length === 0) ? (
                      <p className="text-xs text-slate-500 font-medium">No screenings recorded yet.</p>
                    ) : (
                      <ul className="space-y-2">
                        {data.screenings.map((s: any, i: number) => (
                          <li key={s.id || i} className="border border-slate-200/80 rounded-xl px-3 py-2 text-xs">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-bold text-slate-800">{s.type || s.name || 'Screening'}</span>
                              <span className="text-[10px] font-extrabold uppercase tracking-wide bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{s.result || s.status || '—'}</span>
                            </div>
                            <p className="text-slate-500 mt-0.5">{s.date ? new Date(s.date).toLocaleDateString() : ''}{s.representative ? ` · ${s.representative}` : ''}</p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Section>

                  <Section title="Program confirmation" icon={<CheckCircle2 className="h-4 w-4" />} defaultOpen={true}>
                    {application?.confirmationType === 'none' ? (
                      <p className="text-xs text-slate-500 font-medium">This program has no placement or admission step.</p>
                    ) : (
                      <>
                        <p className="text-xs text-slate-600 font-medium mb-2">
                          Confirmation type: <span className="font-bold text-slate-800">{application?.confirmationType === 'placement' ? 'Placement' : 'Admission'}</span>
                        </p>
                        {placements.length === 0 ? (
                          <p className="text-xs text-slate-500 font-medium">No {application?.confirmationType === 'placement' ? 'placements' : 'admissions'} recorded yet.</p>
                        ) : (
                          <ul className="space-y-2">
                            {placements.map((p: any, i: number) => (
                              <li key={p.id || i} className="border border-slate-200/80 rounded-xl px-3 py-2 text-xs">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-bold text-slate-800">{p.employerName || p.title || p.organization || 'Placement'}</span>
                                  <span className="text-[10px] font-extrabold uppercase tracking-wide bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{p.status || '—'}</span>
                                </div>
                                {(p.location || p.startDate) && (
                                  <p className="text-slate-500 mt-0.5">{[p.location, p.startDate ? new Date(p.startDate).toLocaleDateString() : ''].filter(Boolean).join(' · ')}</p>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </Section>

                  <Section title="Finance" icon={<Landmark className="h-4 w-4" />} defaultOpen={true}>
                    {invoices.length === 0 ? (
                      <p className="text-xs text-slate-500 font-medium">No invoices on file.</p>
                    ) : (
                      <>
                        <ul className="space-y-2">
                          {invoices.map((inv) => {
                            const paidAmt = Number(inv.paidAmount ?? inv.amountPaid ?? 0);
                            const due = Math.max(0, (inv.total || 0) - paidAmt);
                            const cur = (inv as any).currency || 'USD';
                            return (
                              <li key={inv.id} className="border border-slate-200/80 rounded-xl px-3 py-2 text-xs flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="font-bold text-slate-800 truncate">Invoice #{inv.id}</p>
                                  <p className="text-slate-500">Total {fmtMoney(inv.total || 0, cur)} · Paid {fmtMoney(paidAmt, cur)} · Due {fmtMoney(due, cur)}</p>
                                </div>
                                <span className="text-[10px] font-extrabold uppercase tracking-wide bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 shrink-0">{inv.status || '—'}</span>
                              </li>
                            );
                          })}
                        </ul>
                        <p className="text-xs font-bold text-slate-800 mt-2">Outstanding balance: {fmtMoney(outstanding, (invoices[0] as any)?.currency || 'USD')}</p>
                      </>
                    )}
                    <p className="text-[11px] text-slate-500 mt-2">Only reconciled, fully-paid invoices clear the finance gate.</p>
                  </Section>

                  <Section title="Immigration" icon={<Plane className="h-4 w-4" />} defaultOpen={false}>
                    {(() => {
                      const vd = (application as any)?.visaDetails || {};
                      const hasVisaData = vd.visaNumber || vd.visaStatus || vd.embassyAppointmentAt || vd.embassyLocation;
                      return (
                        <>
                          {hasVisaData && (
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-3">
                              {vd.visaNumber && (
                                <div>
                                  <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Visa number</dt>
                                  <dd className="font-semibold text-slate-800">{vd.visaNumber}</dd>
                                </div>
                              )}
                              {vd.visaStatus && (
                                <div>
                                  <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Visa status</dt>
                                  <dd className="font-semibold text-slate-800">{vd.visaStatus}</dd>
                                </div>
                              )}
                              {vd.embassyAppointmentAt && (
                                <div>
                                  <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Embassy appointment</dt>
                                  <dd className="font-semibold text-slate-800">{fmtDate(vd.embassyAppointmentAt)}</dd>
                                </div>
                              )}
                              {vd.embassyLocation && (
                                <div>
                                  <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">Embassy location</dt>
                                  <dd className="font-semibold text-slate-800">{vd.embassyLocation}</dd>
                                </div>
                              )}
                            </dl>
                          )}
                          {visaAttempts.length === 0 && !hasVisaData ? (
                            <p className="text-xs text-slate-500 font-medium">No visa attempts recorded yet.</p>
                          ) : visaAttempts.length > 0 && (
                            <ul className="space-y-2">
                              {visaAttempts.map((v: any, i: number) => (
                                <li key={i} className="border border-slate-200/80 rounded-xl px-3 py-2 text-xs">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-bold text-slate-800">Attempt {v.attemptNumber ?? i + 1}</span>
                                    <span className={`text-[10px] font-extrabold uppercase tracking-wide rounded-full px-2 py-0.5 ${String(v.outcome).toLowerCase() === 'refused' ? 'bg-red-100 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                                      {v.outcome || '—'}
                                    </span>
                                  </div>
                                  <p className="text-slate-500 mt-0.5">{v.date ? new Date(v.date).toLocaleDateString() : ''}</p>
                                  {String(v.outcome).toLowerCase() === 'refused' && (
                                    <p className="text-amber-700 font-medium mt-1">Refused — a retry attempt can be recorded by the team.</p>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      );
                    })()}
                    {embassyAppointment && (
                      <p className="text-xs text-slate-700 mt-2">
                        <span className="font-bold">Embassy appointment:</span> {typeof embassyAppointment === 'string' ? embassyAppointment : fmtDate(embassyAppointment.date)} {embassyAppointment.location ? `· ${embassyAppointment.location}` : ''}
                      </p>
                    )}
                  </Section>

                  <Section title="Travel" icon={<Plane className="h-4 w-4" />} defaultOpen={false}>
                    {(() => {
                      const fd: Record<string, any> = (flightDetails && typeof flightDetails === 'object') ? flightDetails : {};
                      const FIELD_LABELS: Record<string, string> = {
                        airline: 'Airline', flightNumber: 'Flight number',
                        departureAirport: 'Departure airport', arrivalAirport: 'Arrival airport',
                        departureTime: 'Departure', arrivalTime: 'Arrival',
                        departureDate: 'Departure date', arrivalDate: 'Arrival date',
                        notes: 'Notes / arrival contacts', arrivalContacts: 'Arrival contacts',
                        checkedInAt: 'Checked in',
                      };
                      const entries = Object.entries(fd).filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '');
                      if (entries.length === 0) {
                        return <p className="text-xs text-slate-500 font-medium">No flight details on file.</p>;
                      }
                      const fmtVal = (k: string, v: any) => {
                        const s = String(v);
                        if (/time|date|checkedin/i.test(k)) {
                          const d = new Date(s);
                          if (!Number.isNaN(d.getTime())) return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
                        }
                        return s;
                      };
                      return (
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                          {entries.map(([k, v]) => (
                            <div key={k} className={k === 'notes' ? 'col-span-2' : ''}>
                              <dt className="font-bold text-slate-500 uppercase tracking-wide text-[10px]">
                                {FIELD_LABELS[k] || k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                              </dt>
                              <dd className="font-semibold text-slate-800 break-words">{fmtVal(k, v)}</dd>
                            </div>
                          ))}
                        </dl>
                      );
                    })()}
                  </Section>
                </div>
              )}

              {/* TAB 4: ACTIVITY & MESSAGES */}
              {activeTab === 'activity' && (
                <div className="space-y-4 animate-fade-in">
                  <Section title="Communications" icon={<MessageSquare className="h-4 w-4" />} defaultOpen={true}>
                    {!isUnclaimed && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button type="button" onClick={() => setShowNudge(true)} className="px-3 py-2 min-h-[44px] rounded-xl bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer">
                          <Send className="h-3.5 w-3.5" /> Send status email now
                        </button>
                        <button type="button" onClick={() => handlePauseEmails(true)} disabled={!!acting} className="px-3 py-2 min-h-[44px] rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 disabled:opacity-50 cursor-pointer">
                          Pause emails for this applicant
                        </button>
                      </div>
                    )}
                    {(!data.emailLog || data.emailLog.length === 0) ? (
                      <p className="text-xs text-slate-500 font-medium">No emails logged yet.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {data.emailLog.map((e) => (
                          <li key={e.id} className="border border-slate-200/80 rounded-xl px-3 py-2 text-xs flex items-center gap-2">
                            <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="font-bold text-slate-800 truncate">{e.templateKey || 'Email'}</p>
                              <p className="text-slate-500">{fmtDate(e.sentAt)} · {e.trigger || ''}</p>
                            </div>
                            <span className="text-[10px] font-extrabold uppercase tracking-wide bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 shrink-0">{e.status || '—'}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Section>

                  <Section title="Activity Feed" icon={<HistoryIcon className="h-4 w-4" />} defaultOpen={true}>
                    <DrawerActivityFeed uid={uid} refreshSignal={refreshTick} />
                    {data.notes.length > 0 && (
                      <div className="mt-4">
                        <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-2">Notes</p>
                        <ul className="space-y-1.5">
                          {data.notes.map((n) => (
                            <li key={n.id} className="text-xs bg-slate-50 border border-slate-200/70 rounded-xl px-3 py-2">
                              <p className="text-slate-700">{n.body}</p>
                              <p className="text-slate-400 mt-0.5">{n.byName} · {fmtDate(n.at)}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Section>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {record && (
          <div className="bg-white border-t border-slate-200 px-5 py-3.5 shrink-0 flex flex-wrap items-center gap-2">
            {record.overridden && (
              <button type="button" onClick={handleClearOverride} disabled={acting === 'clearing override'} className="px-3 py-2 min-h-[44px] rounded-xl border border-[var(--color-override-border)] text-[var(--color-override)] text-xs font-bold hover:bg-[var(--color-override-bg)] disabled:opacity-50 flex items-center gap-1.5 cursor-pointer">
                {acting === 'clearing override' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Clear override
              </button>
            )}
            {record.demoted && (
              <button type="button" onClick={handleClearDemotion} disabled={acting === 'clearing demotion'} className="px-3 py-2 min-h-[44px] rounded-xl border border-amber-300 text-amber-800 text-xs font-bold hover:bg-amber-50 disabled:opacity-50 flex items-center gap-1.5 cursor-pointer">
                {acting === 'clearing demotion' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Clear demotion
              </button>
            )}
            {!isUnclaimed && (
              <button type="button" onClick={handleRecompute} disabled={acting === 'recomputing'} className="px-3 py-2 min-h-[44px] rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5 cursor-pointer">
                {acting === 'recomputing' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Recompute
              </button>
            )}
            <span className="flex-1" />
            {/* Pinned advance action (2026-10-14): the footer's sticky
                position keeps the primary matriculation action visible
                without scrolling to the gate panel. Same visibility
                condition, same label, same handler as StageGatePanel —
                shared helpers, never a second implementation.
                Hierarchy (2026-10-16): this is THE main button of the
                drawer — largest target, deepest color, grows to fill
                the row on narrow screens; every other footer control is
                a small outline/secondary button. */}
            {!isUnclaimed && shouldShowAdvanceButton(record.readyToAdvance, record.overridden) && (
              <button
                type="button"
                onClick={handlePinnedAdvance}
                disabled={acting === 'advancing'}
                className="flex-1 sm:flex-none px-6 py-3 min-h-[48px] rounded-xl bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50 text-white text-sm font-extrabold shadow-md flex items-center justify-center gap-1.5 cursor-pointer"
              >
                {acting === 'advancing' ? 'Moving…' : `${advanceButtonLabel(record.stage)} →`}
              </button>
            )}
            {uids.length > 1 && onNavigate && (
              <div className="flex items-center gap-1">
                <button type="button" aria-label="Previous applicant (K)" onClick={() => { const i = uids.indexOf(uid); onNavigate(uids[(i - 1 + uids.length) % uids.length]); }} className="p-2 min-w-[44px] min-h-[44px] rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center cursor-pointer">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button type="button" aria-label="Next applicant (J)" onClick={() => { const i = uids.indexOf(uid); onNavigate(uids[(i + 1) % uids.length]); }} className="p-2 min-w-[44px] min-h-[44px] rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center cursor-pointer">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
            {onViewApplicant && (
              <button
                type="button"
                onClick={() => {
                  // Pass the FULL application document (from /full), not just the
                  // workflow record summary — the preview modal renders
                  // personalDetails, documents, etc. Fall back to the record
                  // summary if the full doc hasn't loaded.
                  const fullApp = data?.application;
                  onViewApplicant(fullApp ? { ...fullApp, uid: record.uid } : { id: record.applicationId, uid: record.uid, applicationId: record.applicationId });
                }}
                className="px-3 py-2 min-h-[44px] rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold flex items-center gap-1.5 cursor-pointer"
              >
                <ExternalLink className="h-4 w-4" /> Full profile
              </button>
            )}
          </div>
        )}
      </div>

      {showOverride && record && (
        <OverrideModal
          uid={uid}
          applicantName={record.displayName}
          currentStage={record.stage}
          blockers={record.structuredBlockers}
          prerequisites={record.stagePrerequisites || []}
          onClose={() => setShowOverride(false)}
          onSuccess={() => { setShowOverride(false); onChanged(); if (uid) load(uid); }}
          onOptimisticMove={(stage) => beginOptimisticMove(stage)}
          onMoveFailed={endOptimisticMove}
        />
      )}
      {approveBlock && record && (
        <ApproveOverrideModal
          uid={uid}
          applicantName={record.displayName}
          unmetGates={approveBlock}
          notes={preNotes}
          onClose={() => setApproveBlock(null)}
          onSuccess={() => { setApproveBlock(null); onChanged(); if (uid) load(uid); }}
          onOptimisticMove={() => {
            const i = STAGE_ORDER.indexOf(record.stage as any);
            beginOptimisticMove(i >= 0 && i < STAGE_ORDER.length - 1 ? STAGE_ORDER[i + 1] : undefined);
          }}
          onMoveFailed={endOptimisticMove}
        />
      )}
      {showDemote && record && (
        <DemoteModal
          uid={uid}
          applicantName={record.displayName}
          currentStage={record.stage}
          initialTargetStage={demoteTargetStage}
          onClose={() => { setShowDemote(false); setDemoteTargetStage(undefined); }}
          onSuccess={() => { setShowDemote(false); setDemoteTargetStage(undefined); onChanged(); if (uid) load(uid); }}
          onOptimisticMove={(stage) => beginOptimisticMove(stage)}
          onMoveFailed={endOptimisticMove}
        />
      )}
      {showNudge && record && (
        <NudgeModal
          items={[{ uid, applicationId: record.applicationId, displayName: record.displayName, email: record.email }]}
          onClose={() => setShowNudge(false)}
          onSent={() => { onChanged(); if (uid) load(uid); }}
        />
      )}
      {showAiDraft && record && (
        <AiDraftEmailModal
          uid={uid}
          stageLabel={(STAGE_LABELS as any)[record.stage] || record.stage}
          onClose={() => setShowAiDraft(false)}
          onSent={() => { onChanged(); if (uid) load(uid); }}
        />
      )}
      {showCreateTask && record && (
        <CreateInternalTaskModal
          uid={uid}
          applicationId={record.applicationId}
          applicantName={record.displayName}
          currentStage={record.stage}
          onClose={() => setShowCreateTask(false)}
          onCreated={() => { onChanged(); if (uid) load(uid); }}
        />
      )}
      {assignAutoOpen && record && (
        <AssignAutomationModal
          isOpen={assignAutoOpen}
          onClose={() => setAssignAutoOpen(false)}
          source="drawer"
          referenceApplicant={{
            id: record.applicationId || record.id || uid,
            applicantName: record.displayName || record.applicantName,
            customApplicationId: record.customApplicationId || record.applicationId,
            programName: record.programName || record.program,
            currentStage: record.stage || record.currentStage,
          }}
          onSuccess={() => { onChanged(); if (uid) load(uid); }}
        />
      )}
    </div>
  );
};

// Re-export for the page's bulk-bar nudge modal reuse.
export { NudgeModal as BulkNudgeModal };
